import React, { Suspense } from "react";
import { Route, RouteProps, Redirect } from "react-router";
import SessionManager from "@Core/session";
import responseContext from "@Core/responseContext";
import { useQuery } from "@tanstack/react-query";
import { QueryKeys, ThreeMinutes, UnauthenticatedPath } from "@Constants";
import RoleService from "@Services/RoleService";
import { useContext } from "react";
import SkeletonLayout from "@Components/layouts/SkeletonLayout";
import { MainContext } from "@Context/provider";
import OptionService from "@Services/OptionService";
import { IOptionPayload } from "@Models/IOptionDefinitionDetails";
import ActivityService from "@Services/ActivityService";

export interface IProps extends RouteProps {
    // component?: RouteProps["component"] | React.FC<unknown>;
    layout: React.FC<unknown>;
    statusCode?: number;

    // Pre-fetch global query results before rendering, e.g. roles, options, definitions.
    prefetch?: boolean;
    lazy?: boolean;
}

enum EnabledRoute {
    NotAuthenticated = 0,
    Guest = 1,
    AuthenticatedProfile = 2,
    AuthenticatedDefault = 3,
}

const AppRoute: React.FC<IProps> = ({
    component: Component,
    layout: Layout,
    lazy: isLazy,
    path: Path,
    prefetch,
    render = undefined,
    statusCode,
    ...rest
}: IProps) => {
    const { setCachedPath } = useContext(MainContext);

    const isLoginPath = Path === "/" + UnauthenticatedPath.Login;
    const isRegisterPath = Path === "/" + UnauthenticatedPath.Register;
    const isUpdatePath = Path === "/" + UnauthenticatedPath.PasswordReset; // Complete password reset
    const isResetPath = Path === "/" + UnauthenticatedPath.ResetPassword; // Request password reset
    const isActivatePath = Path === "/" + UnauthenticatedPath.Activate;
    responseContext.statusCode = statusCode;

    let route;
    if (SessionManager.isAuthenticated) {
        if (isLoginPath || isRegisterPath || isResetPath || isUpdatePath || isActivatePath) {
            route = EnabledRoute.AuthenticatedProfile;
        } else {
            route = EnabledRoute.AuthenticatedDefault;
        }
    } else {
        if (isLoginPath || isRegisterPath || isResetPath || isUpdatePath || isActivatePath) {
            route = EnabledRoute.Guest;
        } else {
            route = EnabledRoute.NotAuthenticated;
        }
    }

    // Roles query to fetch all roles for capability checks
    const { data: rolesData, isFetching: _rolesFetching } = useQuery(
        [QueryKeys.Roles],
        async () => {
            const service = new RoleService();
            const result = await service.getAll(true);
            return !result.hasErrors ? result.value : null;
        },
        {
            staleTime: ThreeMinutes,
            refetchOnWindowFocus: false,
            enabled: !!SessionManager.getServiceUser()?.userId && !!prefetch && route !== EnabledRoute.NotAuthenticated,
        }
    );

    // Options query to fetch all user option definitions and user option instances.
    const { data: optionsData, isFetching: _optionsFetching } = useQuery(
        [QueryKeys.UserOptions],
        async () => {
            const optionPayload: IOptionPayload = { definitions: [], instances: [] };
            if (+optionPayload.definitions.length === 0) {
                const service = new OptionService();
                const definitionsResult = await service.getUserDefinitions();
                if (!definitionsResult.hasErrors) {
                    optionPayload.definitions = definitionsResult.value;
                    const instanceResult = await service.getUser(SessionManager.getServiceUser()?.userId);
                    if (!instanceResult.hasErrors) {
                        optionPayload.instances = instanceResult.value;
                    }
                }
            } else {
                const service = new OptionService();
                const instanceResult = await service.getUser(SessionManager.getServiceUser()?.userId);
                if (!instanceResult.hasErrors) {
                    optionPayload.instances = instanceResult.value;
                }
            }
            return optionPayload;
        },
        {
            staleTime: Infinity,
            refetchOnWindowFocus: false,
            enabled: !!SessionManager.getServiceUser()?.userId && prefetch && route !== EnabledRoute.NotAuthenticated,
        }
    );

    const { data: definitionsData, isFetching: _definitionsFetching } = useQuery(
        [QueryKeys.ActivityDefinitions],
        async () => {
            const service = new ActivityService();
            const definitionsResult = await service.getDefinitions();

            if (!definitionsResult.hasErrors) {
                return definitionsResult.value;
            }
            return { products: [], categories: [], activities: [] };
        },
        {
            staleTime: Infinity,
            enabled: !!SessionManager.getServiceUser()?.userId && prefetch && route !== EnabledRoute.NotAuthenticated,
            refetchOnWindowFocus: false,
        }
    );

    const rolesReady = !prefetch || (prefetch && rolesData !== undefined);
    const optionsReady = !prefetch || (prefetch && optionsData !== undefined);
    const definitionsReady = !prefetch || (prefetch && definitionsData !== undefined);

    switch (route) {
        case EnabledRoute.NotAuthenticated: {
            setCachedPath(Path.toString());
            return <Redirect to="/login" />;
        }

        case EnabledRoute.AuthenticatedProfile:
            return <Redirect to="/" />;

        default:
            return (
                <Suspense fallback={<SkeletonLayout />}>
                    {!rolesReady || !optionsReady || !definitionsReady ? (
                        <Route {...rest} render={render ? render : () => <SkeletonLayout />} />
                    ) : (
                        <Route
                            {...rest}
                            render={
                                render
                                    ? render
                                    : (props) => <Layout>{Component ? <Component {...props} /> : <></>}</Layout>
                            }
                        />
                    )}
                </Suspense>
            );
    }
};

export default AppRoute;
